/**
 * VentaHub's plain query strings for the two AMS routes (`AmsViewPage.tsx:614-694`):
 * `?store_ids=38,144&ad_group_id=518346233110905`, where TanStack Router's
 * JSON serialiser writes `store_ids=[38,144]` and `ad_group_id="518…"`.
 *
 * The router serialises every route alike, so the change is a search
 * middleware on these two routes only (`search.middlewares` in their route
 * files): it rewrites the values the router is about to write into ones its
 * serialiser leaves as they are, and every other page keeps its URLs. Each
 * rewrite is one the page's schema reads back to the same value — the
 * schemas already took VentaHub's form from a pasted link — and a value it
 * could not round-trip keeps the JSON form:
 *
 * - an id list becomes `38,144` (a lone id `38`);
 * - a text list becomes `a,b`, unless an entry holds a comma or starts or
 *   ends with a space, which the CSV reading would split or trim;
 * - a text that is a whole number becomes that number, unquoted, when it
 *   reads back exactly — never past `Number.MAX_SAFE_INTEGER`, never with a
 *   leading zero. An Amazon id beyond that keeps its quotes.
 *
 * The commas go out percent-encoded (`38%2C144`), as `URLSearchParams` —
 * VentaHub's writer too — encodes them.
 */

/** A search middleware's view of the URL state: the router's own values. */
type SearchMiddlewareArgs<S> = { search: S; next: (search: S) => S }

/** The keys of a page's search state that hold each kind of value. */
export type PlainKeys<S> = {
  idLists?: readonly (keyof S)[]
  textLists?: readonly (keyof S)[]
  wholeNumberTexts?: readonly (keyof S)[]
}

/** An id list as VentaHub wrote it: `38,144`, a lone id as itself. */
export function plainIdList(ids: readonly number[]): number | string | readonly number[] {
  if (ids.length === 0) return ids
  if (ids.length === 1) return ids[0] as number
  return ids.join(',')
}

/** A text list as VentaHub wrote it, `ENABLED,PAUSED` — when it reads back unchanged. */
export function plainTextList(items: readonly string[]): string | readonly string[] {
  const csvSafe = items.every((item) => !item.includes(',') && item.trim() === item && item !== '')
  return csvSafe && items.length > 0 ? items.join(',') : items
}

/** A whole number written as text, unquoted — when it reads back exactly. */
export function plainText(text: string): string | number {
  if (!/^\d+$/.test(text)) return text
  const value = Number(text)
  return Number.isSafeInteger(value) && String(value) === text ? value : text
}

/**
 * The route middleware writing `keys` of a page's search state in VentaHub's
 * plain form. It runs after the route's own validation, on what is about to
 * be written; the page reads the URL back through its schema as before.
 */
export function plainSearch<S extends object>(keys: PlainKeys<S>) {
  return ({ search, next }: SearchMiddlewareArgs<S>): S => {
    const out: Record<string, unknown> = Object.fromEntries(Object.entries(next(search)))
    const each = (list: readonly (keyof S)[] | undefined, write: (value: unknown) => unknown) => {
      for (const key of list ?? []) {
        const name = String(key)
        if (out[name] !== undefined) out[name] = write(out[name])
      }
    }
    each(keys.idLists, (value) =>
      Array.isArray(value) && value.every((item) => typeof item === 'number')
        ? plainIdList(value)
        : value,
    )
    each(keys.textLists, (value) =>
      Array.isArray(value) && value.every((item) => typeof item === 'string')
        ? plainTextList(value)
        : value,
    )
    each(keys.wholeNumberTexts, (value) => (typeof value === 'string' ? plainText(value) : value))
    // The router serialises what it is handed; the page's schema types it again
    // on the way back in, so only the written form differs from `S` here.
    return out as S
  }
}
